import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Lock, Plus, RotateCcw, ShieldCheck, Ticket, Trash2 } from 'lucide-react'
import { Link } from '../router.js'
import { apiRead, usePermissions } from '../admin/primitives.js'
import { Checkbox, SwitchRow } from '../admin/form.js'
import { SegmentedControl } from '../admin/pickers.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { useConfirm } from '../shell/confirm.js'
import { useSaveBar } from '../shell/editing.js'
import { chargerPreferences, chargerReglages, chargerSalles, ecrirePreferences, ecrireReglages } from './soirees-maison-api.js'
import { publierPreferences, useMoi } from './soirees-maison-hooks.js'
import {
  CANAL_LIBELLE, GardeCles, HEURE_RX, NOM_BILLETTERIE, PHRASE_GEL, instantParis, stockageMemoire,
  type Canal, type Salle,
} from './soirees-maison-model.js'
import {
  LIBELLE_REGLAGE, MODELES_DEFAUT, MODELES_MAX, RAFRAICHISSEMENTS,
  avertissementFormule, changementsPreferences, changementsReglages, correctifPreferences, modeleVierge, modelesDepuisSaisies,
  preferencesInvalides, saisieModele, tvaAConfirmer, tvaDefautDe, valeurAffichee,
  type GenreHeure, type HeureSaisie, type ModeleSaisie, type ModeleTarif, type Preferences, type Rafraichissement, type ReglageBilletterie,
} from './reglages-billetterie-model.js'

/**
 * ADMINISTRATION › RÉGLAGES › BILLETTERIE (contrat `soirees-v1` §7).
 *
 * Deux sections, une seule Save Bar (une unité d'édition par route) :
 *  · « Dans Gestion » : le GEL des modifications faites depuis Gestion
 *    (indépendant du Mode sûr, qui couperait aussi la porte), l'encart, les
 *    soirées d'entraînement, le rafraîchissement, le pré-remplissage de
 *    l'assistant et les modèles de tarifs ;
 *  · « Chez Billetterie Live Club » : ses réglages, lus et écrits par son API,
 *    jamais copiés ici. Seules trois clés s'écrivent depuis Gestion ; les autres
 *    sont affichées grisées, avec leur raison.
 * Les deux écritures demandent une confirmation d'identité.
 *
 * L'enregistrement n'envoie que ce qui a changé depuis la lecture, avec
 * l'heure de la ligne lue : un onglet ouvert avant un gel posé ailleurs (le
 * téléphone, un autre administrateur) ne le lève jamais en enregistrant autre
 * chose — le serveur refuse, et l'écran demande de recharger.
 */

interface Brouillon { prefs: Preferences; modeles: ModeleSaisie[]; maj_a: string | null }

const CANAUX_OPTIONS: { value: Canal; label: string }[] = [
  { value: 'SUR_PLACE', label: CANAL_LIBELLE.SUR_PLACE },
  { value: 'EN_LIGNE', label: CANAL_LIBELLE.EN_LIGNE },
  { value: 'INVITATION', label: CANAL_LIBELLE.INVITATION },
]

const RAFRAICHISSEMENT_OPTIONS = RAFRAICHISSEMENTS.map((r) => ({ value: String(r) as `${Rafraichissement}`, label: `${r} s` }))

interface ModeSur { effectif: string | null; libelle: string | null }

export function ReglagesBilletteriePage() {
  const { permissions } = usePermissions()
  const moi = useMoi()
  const confirm = useConfirm()
  const peutRegler = permissions.includes('ticketing.settings.manage')
  const [lu, setLu] = useState<Brouillon | null>(null)
  const [v, setV] = useState<Brouillon | null>(null)
  const [gelIllisible, setGelIllisible] = useState(false)
  const [erreurPrefs, setErreurPrefs] = useState<string | null>(null)
  const [reglages, setReglages] = useState<ReglageBilletterie[] | null>(null)
  const [erreurReglages, setErreurReglages] = useState<string | null>(null)
  const [saisies, setSaisies] = useState<Record<string, string>>({})
  const [salles, setSalles] = useState<Salle[] | null>(null)
  const [modeSur, setModeSur] = useState<ModeSur | null>(null)
  const [defauts, setDefauts] = useState<ModeleTarif[] | null>(null)
  const memoire = useRef(stockageMemoire())
  const cles = useMemo(() => new GardeCles(memoire.current, moi ?? 'inconnu'), [moi])

  const appliquerPrefs = useCallback((p: Preferences, illisible: boolean, majA: string | null) => {
    const b = { prefs: p, modeles: p.modeles_tarifs.map(saisieModele), maj_a: majA }
    setLu(b); setV(b); setGelIllisible(illisible); setErreurPrefs(null)
    publierPreferences(p)
  }, [])
  const lireReglagesBilletterie = useCallback(() => {
    void chargerReglages().then((r) => {
      if (r.ok) {
        setReglages(r.data.reglages); setErreurReglages(null)
        setSaisies(Object.fromEntries(r.data.reglages.filter((x) => x.modifiable).map((x) => [x.cle, valeurAffichee(x)])))
      } else setErreurReglages(r.erreur.message)
    })
  }, [])
  useEffect(() => {
    void chargerPreferences().then((r) => {
      if (!r.ok) { setErreurPrefs(r.erreur.message); return }
      appliquerPrefs(r.data.preferences, r.data.gel_illisible, r.data.maj_a)
      if (r.data.mode_sur) setModeSur({ effectif: null, libelle: r.data.mode_sur.libelle })
      setDefauts(r.data.modeles_defaut)
    })
    lireReglagesBilletterie()
    void chargerSalles().then((r) => { if (r.ok) setSalles(r.data) })
  }, [appliquerPrefs, lireReglagesBilletterie])
  // Le Mode sûr effectif : rendu avec les réglages ; à défaut, lu là où il se règle (droit « intégrations »).
  useEffect(() => {
    if (modeSur || !permissions.includes('integration.read')) return
    void apiRead<{ integrations?: { key: string; effective: string | null }[]; labels?: Record<string, string> }>('/api/v1/settings/safe-mode')
      .then((r) => {
        const row = r.status === 200 ? r.data.integrations?.find((i) => i.key === 'billetterie') : undefined
        if (row) setModeSur((x) => x ?? { effectif: row.effective, libelle: row.effective ? r.data.labels?.[row.effective] ?? row.effective : null })
      }).catch(() => undefined)
  }, [permissions]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── ce qui change, ce qui part ─── */
  const modeles = v ? modelesDepuisSaisies(v.modeles) : null
  const assemble = v && modeles && 'modeles' in modeles ? { ...v.prefs, modeles_tarifs: modeles.modeles } : null
  const nPrefs = lu && v ? (assemble ? changementsPreferences(lu.prefs, assemble) : JSON.stringify(lu) === JSON.stringify(v) ? 0 : 1) : 0
  const ch = reglages ? changementsReglages(reglages, saisies) : { changements: [] }
  const nReglages = 'changements' in ch ? ch.changements.length : 1
  const prefsAReecrire = gelIllisible && nPrefs === 0
  const n = nPrefs + nReglages + (prefsAReecrire ? 1 : 0)
  const invalide = !peutRegler ? null
    : modeles && 'erreur' in modeles ? modeles.erreur
      : assemble ? preferencesInvalides(assemble) ?? ('erreur' in ch ? ch.erreur : null)
        : 'erreur' in ch ? ch.erreur : null

  useSaveBar({
    dirty: peutRegler && n > 0,
    count: n,
    invalid: invalide,
    saveLabel: 'Enregistrer',
    successMessage: 'Réglages de Billetterie Live Club enregistrés.',
    onCancel: () => {
      if (lu) setV(lu)
      if (reglages) setSaisies(Object.fromEntries(reglages.filter((x) => x.modifiable).map((x) => [x.cle, valeurAffichee(x)])))
    },
    onSave: async () => {
      if (invalide) return invalide
      if ((nPrefs > 0 || prefsAReecrire) && assemble && lu) {
        // Les seules clés changées (le gel seulement s'il a été touché), et l'heure de la ligne lue.
        const corps = { ...correctifPreferences(lu.prefs, assemble, { gelIllisible }), maj_a_lue: lu.maj_a }
        const r = await ecrirePreferences(corps, cles.pour('preferences', JSON.stringify(corps)))
        if (!r.ok) return r.erreur.message
        cles.oublier('preferences')
        appliquerPrefs(r.data.preferences, r.data.gel_illisible, r.data.maj_a)
      }
      if ('changements' in ch && ch.changements.length > 0) {
        const r = await ecrireReglages(ch.changements, cles.pour('reglages', JSON.stringify(ch.changements)))
        if (!r.ok) {
          const deja = nPrefs > 0 ? 'Les réglages de Gestion sont enregistrés ; ceux de la billetterie, non : ' : ''
          if (r.erreur.genre === 'version') lireReglagesBilletterie()
          return `${deja}${r.erreur.message}`
        }
        cles.oublier('reglages')
        lireReglagesBilletterie()
      }
      return true
    },
  })

  const setP = (f: (p: Preferences) => Preferences) => setV((x) => (x ? { ...x, prefs: f(x.prefs) } : x))
  const setModele = (i: number, f: (m: ModeleSaisie) => ModeleSaisie) => setV((x) => (x ? { ...x, modeles: x.modeles.map((m, j) => (j === i ? f(m) : m)) } : x))
  const deplacer = (i: number, d: -1 | 1) => setV((x) => {
    if (!x) return x
    const l = [...x.modeles]; const j = i + d
    if (j < 0 || j >= l.length) return x
    ;[l[i], l[j]] = [l[j]!, l[i]!]
    return { ...x, modeles: l }
  })
  const remettreModeles = async () => {
    if (!(await confirm({
      title: 'Remettre les modèles proposés ?', variant: 'sensitive', confirmLabel: 'Remettre les modèles',
      message: 'Les modèles actuels sont remplacés par les dix modèles proposés (prix d’après Shotgun). Rien n’est enregistré avant « Enregistrer ». Les soirées déjà créées ne changent pas.',
    }))) return
    setV((x) => (x ? { ...x, modeles: (defauts ?? MODELES_DEFAUT).map(saisieModele) } : x))
  }

  const p = v?.prefs
  const ro = !peutRegler
  const nouvelle = p?.nouvelle_soiree
  const bascule = reglages?.find((r) => r.cle === 'nuit.heure_bascule')
  // Le taux réglé chez la billetterie, tel que lu (pas la saisie en cours) : jamais un chiffre écrit en dur.
  const tvaBp = reglages ? tvaDefautDe(reglages) : null
  const tvaDefaut = tvaBp === null ? null : `${valeurAffichee({ cle: 'tarif.tva_bp_defaut', valeur: tvaBp })} %`

  return (
    <main className="lc-page lc-page--readable lc-stagger bs-page">
      <SettingsCrumb />
      <h1 className="lc-title bs-titre-icone"><Ticket size={26} aria-hidden="true" /> Billetterie</h1>
      <p className="lc-subtitle">
        Les soirées de {NOM_BILLETTERIE} depuis Gestion : le gel des modifications, l’assistant « Nouvelle soirée », les modèles de tarifs, et les réglages de la billetterie elle-même.
      </p>
      {ro ? <p className="lc-hint bs-lecture-seule">Lecture seule : seul un compte autorisé à « Régler Billetterie Live Club » modifie ces réglages.</p> : null}
      <p className="lc-hint">Le raccordement (adresse, jeton) se règle dans <Link href="/settings/integrations/billetterie">Intégrations › Billetterie</Link>.</p>

      {/* ═══ DANS GESTION ═══ */}
      <section className="lc-panel bs-section" aria-labelledby="bs-r-gestion">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="bs-r-gestion">Dans Gestion</h2></header>
        <div className="lc-panel__body bs-form">
          {erreurPrefs ? <p className="bs-note bs-note--doute" role="status">{erreurPrefs}</p> : null}
          {!p || !nouvelle ? (!erreurPrefs ? <div className="so-skel" aria-busy="true"><span /><span /></div> : null) : (
            <>
              <h3 className="bs-sous-titre">Le gel</h3>
              {gelIllisible ? <p className="bs-note bs-note--ko" role="alert">Réglage du gel illisible : écritures gelées par prudence. Réenregistrez-le ici.</p> : null}
              <SwitchRow checked={p.ecritures_autorisees} disabled={ro} onChange={(x) => setP((q) => ({ ...q, ecritures_autorisees: x }))}
                title="Modifications depuis Gestion autorisées"
                desc={p.ecritures_autorisees ? 'Les personnes autorisées préparent et mettent en vente depuis Gestion.' : 'Gelées : plus aucun geste depuis Gestion. La page de secours, la porte et les téléphones LC Porte continuent.'} />
              {!p.ecritures_autorisees ? (
                <div className="lc-field">
                  <label className="lc-label" htmlFor="bs-r-motif">Motif du gel (affiché dans chaque refus)</label>
                  <input id="bs-r-motif" className="lc-input" maxLength={200} disabled={ro} value={p.motif_gel ?? ''} onChange={(e) => setP((q) => ({ ...q, motif_gel: e.target.value }))} />
                </div>
              ) : null}
              {lu && !lu.prefs.ecritures_autorisees && lu.prefs.gele_par ? (
                <p className="lc-hint">Gelées par {lu.prefs.gele_par}{lu.prefs.gele_a ? ` le ${instantParis(lu.prefs.gele_a)}` : ''}{lu.prefs.motif_gel ? ` : « ${lu.prefs.motif_gel} »` : ''}.</p>
              ) : null}
              <p className="bs-note bs-note--doute">{PHRASE_GEL}</p>
              <p className="lc-hint">
                Le Mode sûr est un autre frein : en « Lecture seule », il coupe aussi la connexion des téléphones de la porte par Gestion. Le gel, lui, ne touche jamais la porte.
              </p>

              <h3 className="bs-sous-titre">Affichage</h3>
              <SwitchRow checked={p.encart_demo} disabled={ro} onChange={(x) => setP((q) => ({ ...q, encart_demo: x }))}
                title="Encart « Billetterie Live Club · démo » sur la page Soirées" desc="Les prochaines soirées de la billetterie, leur état et leurs ventes face à la jauge." />
              <SwitchRow checked={p.entrainement_visible} disabled={ro} onChange={(x) => setP((q) => ({ ...q, entrainement_visible: x }))}
                title="Montrer les soirées d’entraînement" desc="Elles se gèrent depuis la page de secours ; masquées par défaut dans Gestion." />
              <div className="lc-field">
                <span className="lc-label">Relecture de l’encart et des fiches</span>
                {ro ? <span className="lc-pill lc-pill--idle">{p.rafraichissement_s} s</span>
                  : <SegmentedControl value={String(p.rafraichissement_s) as `${Rafraichissement}`} options={RAFRAICHISSEMENT_OPTIONS}
                    onChange={(x) => setP((q) => ({ ...q, rafraichissement_s: Number(x) as Rafraichissement }))} ariaLabel="Relecture" />}
              </div>

              <h3 className="bs-sous-titre">Nouvelle soirée (pré-remplissage de l’assistant)</h3>
              <div className="bs-grille">
                <div className="lc-field">
                  <label className="lc-label" htmlFor="bs-r-ouv">Ouverture</label>
                  <input id="bs-r-ouv" className="lc-input" type="time" step={60} disabled={ro} value={nouvelle.ouverture} onChange={(e) => setP((q) => ({ ...q, nouvelle_soiree: { ...q.nouvelle_soiree, ouverture: e.target.value } }))} />
                </div>
                <div className="lc-field">
                  <label className="lc-label" htmlFor="bs-r-ferm">Fermeture</label>
                  <input id="bs-r-ferm" className="lc-input" type="time" step={60} disabled={ro} value={nouvelle.fermeture} onChange={(e) => setP((q) => ({ ...q, nouvelle_soiree: { ...q.nouvelle_soiree, fermeture: e.target.value } }))} />
                </div>
                <div className="lc-field">
                  <label className="lc-label" htmlFor="bs-r-jauge">Jauge totale proposée</label>
                  <input id="bs-r-jauge" className="lc-input" inputMode="numeric" placeholder="à saisir" disabled={ro}
                    value={nouvelle.jauge_totale === null ? '' : String(nouvelle.jauge_totale)}
                    onChange={(e) => { const t = e.target.value.trim(); setP((q) => ({ ...q, nouvelle_soiree: { ...q.nouvelle_soiree, jauge_totale: t === '' ? null : /^\d{1,5}$/.test(t) ? Number(t) : q.nouvelle_soiree.jauge_totale } })) }} />
                  <span className="lc-helper">Vide : à saisir dans l’assistant.</span>
                </div>
              </div>
              <div className="lc-field">
                <span className="lc-label">Salles cochées d’office</span>
                <span className="bs-cases">
                  {(salles ?? []).filter((s) => s.active).map((s) => (
                    <Checkbox key={s.code} label={s.nom} disabled={ro} checked={nouvelle.salles.includes(s.code)}
                      onChange={(on) => setP((q) => ({ ...q, nouvelle_soiree: { ...q.nouvelle_soiree, salles: on ? [...q.nouvelle_soiree.salles, s.code] : q.nouvelle_soiree.salles.filter((c) => c !== s.code) } }))} />
                  ))}
                </span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ═══ MODÈLES DE TARIFS ═══ */}
      {v ? (
        <section className="lc-panel bs-section" aria-labelledby="bs-r-modeles">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="bs-r-modeles">Modèles de tarifs ({v.modeles.length})</h2>
            <span className="lc-panel__spacer" />
            {!ro ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { void remettreModeles() }}><RotateCcw size={14} aria-hidden="true" /> Remettre les modèles proposés</button> : null}
          </header>
          <div className="lc-panel__body bs-form">
            <p className="lc-hint">
              Ils ne font que pré-remplir l’assistant : une fois la soirée créée, plus rien ne relie un tarif à son modèle. Les prix proposés viennent des tarifs Shotgun des 120 derniers jours.
              Les heures sont relatives à la soirée ; l’assistant les pose sur la nuit choisie.
            </p>
            <div className="bs-modeles">
              {v.modeles.map((m, i) => (
                <ModeleEditeur key={`${m.id}-${i}`} m={m} rang={i} total={v.modeles.length} ro={ro} tvaDefaut={tvaDefaut}
                  onChange={(f) => setModele(i, f)} onDeplacer={(d) => deplacer(i, d)}
                  onSupprimer={() => setV((x) => (x ? { ...x, modeles: x.modeles.filter((_, j) => j !== i) } : x))} />
              ))}
            </div>
            {!ro ? (
              <button type="button" className="lc-btn" disabled={v.modeles.length >= MODELES_MAX}
                onClick={() => setV((x) => (x ? { ...x, modeles: [...x.modeles, saisieModele(modeleVierge(x.modeles))] } : x))}>
                <Plus size={15} aria-hidden="true" /> Ajouter un modèle
              </button>
            ) : null}
            {v.modeles.length >= MODELES_MAX ? <p className="lc-hint">{MODELES_MAX} modèles au plus.</p> : null}
          </div>
        </section>
      ) : null}

      {/* ═══ CHEZ LA BILLETTERIE ═══ */}
      <section className="lc-panel bs-section" aria-labelledby="bs-r-billetterie">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="bs-r-billetterie">Chez {NOM_BILLETTERIE}</h2></header>
        <div className="lc-panel__body bs-form">
          <p className="lc-hint">Lus et écrits directement chez la billetterie, jamais copiés dans Gestion.</p>
          {erreurReglages ? <p className="bs-note bs-note--doute" role="status">{erreurReglages}</p> : null}
          {!reglages && !erreurReglages ? <div className="so-skel" aria-busy="true"><span /><span /></div> : null}
          {reglages ? reglages.filter((r) => r.cle !== 'nuit.heure_bascule').map((r) => {
            const l = LIBELLE_REGLAGE[r.cle] ?? { titre: r.libelle, aide: '', unite: '' }
            return (
              <div key={r.cle} className={`lc-field bs-reglage${r.modifiable ? '' : ' bs-reglage--fige'}`}>
                <label className="lc-label" htmlFor={`bs-r-${r.cle}`}>{l.titre}</label>
                {r.modifiable ? (
                  <span className="bs-champ__ligne">
                    <input id={`bs-r-${r.cle}`} className="lc-input bs-court" inputMode="decimal" disabled={ro} value={saisies[r.cle] ?? ''}
                      onChange={(e) => setSaisies((s) => ({ ...s, [r.cle]: e.target.value }))} />
                    {l.unite ? <span className="lc-hint">{l.unite}</span> : null}
                  </span>
                ) : (
                  <span className="bs-champ__ligne"><Lock size={14} aria-hidden="true" /> <strong>{valeurAffichee(r)}{l.unite ? ` ${l.unite}` : ''}</strong></span>
                )}
                <span className="lc-helper">
                  {r.modifiable ? `${l.aide} Entre ${valeurAffichee({ cle: r.cle, valeur: r.min })} et ${valeurAffichee({ cle: r.cle, valeur: r.max })}.` : `${l.aide ? `${l.aide} ` : ''}${r.raison ?? 'Se règle depuis la page de secours de la billetterie.'}`}
                  {r.maj_a ? ` Modifié le ${instantParis(r.maj_a)}.` : ''}
                </span>
              </div>
            )
          }) : null}
        </div>
      </section>

      {/* ═══ EN LECTURE ═══ */}
      <section className="lc-panel bs-section" aria-labelledby="bs-r-lecture">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="bs-r-lecture">Ce qui ne se règle pas ici</h2></header>
        <div className="lc-panel__body bs-form">
          <dl className="bs-faits">
            <div><dt>Bascule de la nuit</dt><dd><Lock size={13} aria-hidden="true" /> Figée à {bascule ? valeurAffichee(bascule) : '8'} h, comme partout dans Gestion : avant 8 h du matin, on est encore la nuit de la veille. La changer d’un seul côté décalerait les nuits.</dd></div>
            <div>
              <dt>Mode sûr</dt>
              <dd>
                <ShieldCheck size={13} aria-hidden="true" /> {modeSur?.libelle ? `${modeSur.libelle} pour la billetterie.` : 'Se règle dans Réglages › Général.'} Lecture seule coupe aussi les téléphones de la porte.
                {' '}<Link href="/settings/general#safe-mode">Voir le Mode sûr</Link>
              </dd>
            </div>
            <div>
              <dt>Salles</dt>
              <dd>
                {salles === null ? '…' : salles.length === 0 ? 'Aucune salle lue.' : salles.map((s) => `${s.nom}${s.jauge_defaut !== null ? ` (jauge ${s.jauge_defaut})` : ''}${s.active ? '' : ' — inactive'}`).join(' · ')}
                . Elles se gèrent depuis la page de secours de la billetterie.
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </main>
  )
}

/* ═══════════════════════ un modèle ═══════════════════════ */

const GENRES_HEURE: { value: GenreHeure; label: string }[] = [
  { value: 'aucune', label: 'Aucune' },
  { value: 'ouverture', label: 'À l’ouverture' },
  { value: 'nuit', label: 'Heure de la nuit' },
  { value: 'jours', label: 'Jours avant' },
]

function HeureRelativeChamp({ id, label, h, ro, onChange }: { id: string; label: string; h: HeureSaisie; ro: boolean; onChange: (h: HeureSaisie) => void }) {
  return (
    <div className="lc-field">
      <span className="lc-label" id={`${id}-l`}>{label}</span>
      {ro ? <span className="lc-pill lc-pill--idle">{GENRES_HEURE.find((g) => g.value === h.genre)?.label}</span>
        : <SegmentedControl value={h.genre} options={GENRES_HEURE} onChange={(g) => onChange({ ...h, genre: g })} ariaLabel={label} size="sm" />}
      {h.genre === 'nuit' || h.genre === 'jours' ? (
        <span className="bs-champ__ligne">
          {h.genre === 'jours' ? (
            <input className="lc-input bs-court" inputMode="numeric" aria-label={`${label} : jours avant la nuit`} disabled={ro} value={h.jours} onChange={(e) => onChange({ ...h, jours: e.target.value })} />
          ) : null}
          {h.genre === 'jours' ? <span className="lc-hint">jour(s) avant, à</span> : null}
          <input className="lc-input bs-court" type="time" step={60} aria-label={`${label} : heure`} disabled={ro} value={h.heure} onChange={(e) => onChange({ ...h, heure: e.target.value })} />
        </span>
      ) : null}
      <span className="lc-helper">{h.genre === 'nuit' ? 'Avant 8 h, c’est le lendemain de la nuit.' : h.genre === 'ouverture' ? 'L’heure d’ouverture de la soirée.' : h.genre === 'jours' ? 'Compté sur le calendrier, à partir de la nuit.' : ' '}</span>
    </div>
  )
}

function ModeleEditeur({ m, rang, total, ro, tvaDefaut, onChange, onDeplacer, onSupprimer }: {
  m: ModeleSaisie; rang: number; total: number; ro: boolean
  /** « 5,5 % » : le réglage de la billetterie, lu ; null tant qu'il ne l'est pas. */
  tvaDefaut: string | null
  onChange: (f: (m: ModeleSaisie) => ModeleSaisie) => void; onDeplacer: (d: -1 | 1) => void; onSupprimer: () => void
}) {
  const base = `bs-m-${rang}`
  const set = <K extends keyof ModeleSaisie>(k: K, x: ModeleSaisie[K]) => onChange((y) => ({ ...y, [k]: x }))
  const formule = avertissementFormule({ nom: m.nom, libelle: m.libelle, id: m.id })
  const prix = m.prix_a_saisir ? 'prix à saisir' : m.prix.trim() ? `${m.prix.trim()} €` : 'sans prix'
  return (
    <details className="bs-modele">
      <summary className="bs-modele__resume">
        <strong>{m.libelle || m.nom}</strong>
        <span className="lc-hint">{m.nom} · {CANAL_LIBELLE[m.canal]} · {prix}{m.coche ? ' · coché d’office' : ''}</span>
      </summary>
      <div className="bs-modele__corps bs-form">
        <div className="bs-grille">
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-lib`}>Libellé dans l’assistant</label>
            <input id={`${base}-lib`} className="lc-input" maxLength={60} disabled={ro} value={m.libelle} onChange={(e) => set('libelle', e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-nom`}>Nom du tarif</label>
            <input id={`${base}-nom`} className="lc-input" maxLength={80} disabled={ro} value={m.nom} onChange={(e) => set('nom', e.target.value)} />
          </div>
        </div>
        <Checkbox checked={m.coche} disabled={ro} onChange={(x) => set('coche', x)} label="Coché d’office dans l’assistant" />
        <div className="lc-field">
          <span className="lc-label">Canal</span>
          {ro ? <span className="lc-pill lc-pill--idle">{CANAL_LIBELLE[m.canal]}</span>
            : <SegmentedControl value={m.canal} options={CANAUX_OPTIONS} ariaLabel="Canal" onChange={(c) => onChange((y) => ({ ...y, canal: c, frais: c === 'EN_LIGNE' ? y.frais : '', exoneration: c === 'SUR_PLACE' ? y.exoneration : false }))} />}
        </div>
        <div className="bs-grille">
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-prix`}>Prix proposé (€)</label>
            <input id={`${base}-prix`} className="lc-input" inputMode="decimal" disabled={ro || m.prix_a_saisir} value={m.prix_a_saisir ? '' : m.prix} placeholder={m.prix_a_saisir ? 'à saisir' : ''} onChange={(e) => set('prix', e.target.value)} />
            <Checkbox checked={m.prix_a_saisir} disabled={ro} onChange={(x) => set('prix_a_saisir', x)} label="À saisir dans l’assistant" />
          </div>
          {m.canal === 'EN_LIGNE' ? (
            <div className="lc-field">
              <label className="lc-label" htmlFor={`${base}-frais`}>Frais de service (€)</label>
              <input id={`${base}-frais`} className="lc-input" inputMode="decimal" disabled={ro} value={m.frais} onChange={(e) => set('frais', e.target.value)} />
            </div>
          ) : null}
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-tva`}>TVA (%)</label>
            <input id={`${base}-tva`} className="lc-input" inputMode="decimal" disabled={ro} placeholder="réglage par défaut" value={m.tva} onChange={(e) => set('tva', e.target.value)} />
            <span className="lc-helper">{tvaAConfirmer({ nom: m.nom, id: m.id }) ? 'Formule avec boissons : taux à confirmer par la compta.' : `Vide : le réglage de la billetterie${tvaDefaut ? ` (${tvaDefaut})` : ''}.`}</span>
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-quota`}>Quota (billets)</label>
            <input id={`${base}-quota`} className="lc-input" inputMode="numeric" disabled={ro || m.quota_a_saisir} placeholder={m.quota_a_saisir ? 'à saisir' : 'sans quota'} value={m.quota_a_saisir ? '' : m.quota} onChange={(e) => set('quota', e.target.value)} />
            <Checkbox checked={m.quota_a_saisir} disabled={ro} onChange={(x) => set('quota_a_saisir', x)} label="À saisir dans l’assistant" />
          </div>
        </div>
        <div className="bs-grille">
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-grp`}>Cascade : groupe</label>
            <input id={`${base}-grp`} className="lc-input" maxLength={32} disabled={ro} value={m.cascade_groupe} onChange={(e) => set('cascade_groupe', e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-rang`}>Cascade : rang</label>
            <input id={`${base}-rang`} className="lc-input" inputMode="numeric" disabled={ro} value={m.cascade_rang} onChange={(e) => set('cascade_rang', e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor={`${base}-entree`}>Heure limite d’entrée</label>
            <input id={`${base}-entree`} className="lc-input" type="time" step={60} disabled={ro} value={m.entree_avant} onChange={(e) => set('entree_avant', e.target.value)} />
            <span className="lc-helper">{m.entree_avant && !HEURE_RX.test(m.entree_avant) ? 'Heure illisible.' : 'Vide : aucune.'}</span>
          </div>
        </div>
        <div className="bs-grille">
          <HeureRelativeChamp id={`${base}-deb`} label="Début de vente" h={m.vente_debut} ro={ro} onChange={(h) => set('vente_debut', h)} />
          <HeureRelativeChamp id={`${base}-fin`} label="Fin de vente" h={m.vente_fin} ro={ro} onChange={(h) => set('vente_fin', h)} />
        </div>
        {m.canal === 'SUR_PLACE' ? <SwitchRow checked={m.exoneration} disabled={ro} onChange={(x) => set('exoneration', x)} title="Exonération" desc="Entrée gratuite, motif demandé à la caisse." /> : null}
        <SwitchRow checked={m.cache} disabled={ro} onChange={(x) => set('cache', x)} title="Caché" desc="Enregistré, mais rien ne le lit encore." />
        <div className="lc-field">
          <label className="lc-label" htmlFor={`${base}-consigne`}>Consigne pour la porte</label>
          <input id={`${base}-consigne`} className="lc-input" maxLength={80} disabled={ro} value={m.consigne_porte} onChange={(e) => set('consigne_porte', e.target.value)} />
        </div>
        {formule ? <p className="bs-note bs-note--doute">{formule}</p> : null}
        {!ro ? (
          <div className="bs-modele__actions">
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={rang === 0} onClick={() => onDeplacer(-1)}><ArrowUp size={14} aria-hidden="true" /> Monter</button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={rang === total - 1} onClick={() => onDeplacer(1)}><ArrowDown size={14} aria-hidden="true" /> Descendre</button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm lc-btn--danger-text" onClick={onSupprimer}><Trash2 size={14} aria-hidden="true" /> Supprimer le modèle</button>
          </div>
        ) : null}
      </div>
    </details>
  )
}
